import { Injectable } from '@angular/core';

import { EMPTY, merge, Observable, Subscription } from 'rxjs';
import { switchMap, map, distinctUntilChanged } from 'rxjs/operators';
import { tag } from 'rxjs-spy/operators';

import { PushBus } from 'rev-shared/push/PushBus.Service';
import { SidebarNotification } from 'rev-shared/ui/sidebarLayout/SidebarNotification';

import { INotifications } from './Webcast.Contract';
import { WebcastModel } from './model/WebcastModel';


@Injectable({
	providedIn: 'root'
})
export class WebcastSidebarNotificationsService {
	constructor(
		private readonly PushBus: PushBus
	) {}

	public getSidebarNotifications(webcast: WebcastModel, onlyWebcastEngagementUsage?: boolean): INotifications {
		const chat = new SidebarNotification();
		const questions = new SidebarNotification();
		const polls = new SidebarNotification();

		const subscription = merge(
			this.subscribeChat(webcast, chat),
			this.getQuestionsNotification(webcast, questions),
			this.getPollsNotification(webcast, polls, onlyWebcastEngagementUsage)
		).pipe(tag('WebcastSidebarNotificationsService.getSidebarNotifications'))
			.subscribe();

		return { chat, questions, polls, subscription };
	}

	private subscribeChat(webcast: WebcastModel, notification: SidebarNotification): Observable<any> {
		return webcast.webcast$.pipe(
			map(() => webcast.chatEnabled),
			distinctUntilChanged(),
			switchMap(chatEnabled => chatEnabled ?
				this.PushBus.getObservable(webcast.id, undefined, {
					WebcastCommentAdded: this.getAddNotificationHandler(notification)
				}) :
				EMPTY
			)
		);
	}

	private getQuestionsNotification(webcast: WebcastModel, notification: SidebarNotification): Observable<any> {
		return webcast.webcast$.pipe(
			map(() => webcast.questionAndAnswerEnabled && webcast.currentUser.canManageQuestions),
			distinctUntilChanged(),
			switchMap(qaEnabled => qaEnabled ?
				this.PushBus.getObservable(webcast.id, 'Webcast.Moderator', {
					WebcastQuestionCreated: this.getAddNotificationHandler(notification),
					WebcastQuestionCreatedApi: this.getAddNotificationHandler(notification)
				}) :
				EMPTY
			)
		);
	}

	private getPollsNotification(webcast: WebcastModel, notification: SidebarNotification, onlyWebcastEngagementUsage: boolean): Observable<any> {
		const handler = () => {
			notification.setIcon();
		};

		return webcast.webcast$.pipe(
			map(() => webcast.pollsEnabled && (!webcast.currentUser.canManagePolls || onlyWebcastEngagementUsage)), //only attendees see poll notifications
			distinctUntilChanged(),
			switchMap(enableNotifications => enableNotifications ?
				this.PushBus.getObservable(webcast.id, undefined, {
					PollOpened: handler,
					PollPublished: handler,
					WebcastPollsAdded: handler,
					WebcastPollsUpdated: handler,
					WebcastEnded: e => {
						if(e.shouldResetPolls) {
							notification.clear();
						}
						return EMPTY;
					}
				}) :
				EMPTY
			)
		);
	}

	private getAddNotificationHandler(notification: SidebarNotification): () => Observable<void> {
		return () => {
			notification.addNotification();
			return EMPTY;
		};
	}
}
